/**
 *
 *  Booking
 *  HOC component
 *
 *  Handles the booking depending on status.
 *
 *  Created by Vladimir Shishlyannikov on 14/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect } from "react";
import { withRouter } from "react-router-dom";

import { StateContext } from "../../state";
import { getLastUsedLocation } from "../../utils";

class BookingContainer extends React.Component {
  static propTypes = {};

  static contextType = StateContext;

  createConfirmedBooking(isRepeatBooking) {
    const { user, partnerSummary } = this.context;

    var query = new URLSearchParams(this.props.location.search),
      productId = query.get("product"),
      membershipId = query.get("membershipId"),
      eventId = query.get("eventId"),
      tier = query.get("tier"),
      postalCode = query.get("postal_code"),
      place = query.get("place"),
      placeName = query.get("place_name"),
      location = query.get("location"),
      address = query.get("address"),
      detail = query.get("detail"),
      startTime = query.get("start_time"),
      region = query.get("region"),
      city = query.get("city");

    if (!productId && !(membershipId || eventId)) {
      console.error("Product is required in order to create a booking.");

      return;
    }

    // TODO make more robust, copy/ paste from OurTrainers

    const lastUsedLocation = getLastUsedLocation(user); //JSON.parse(localStorage.getItem(storageKey));

    let latitude, longitude;

    if (location) {
      [latitude, longitude] = location.split(",");
    }

    let confirmedBooking;

    if (membershipId) {
      confirmedBooking = {
        isCreated: true,
        customerId: user.id,
        membershipId: Number(membershipId),
        partnerId: partnerSummary.id,
        region,
        city,
        // start: new Date(Number(startTime)).toISOString(),
      };
    } else if (eventId) {
      confirmedBooking = {
        isCreated: true,
        customerId: user.id,
        eventId: Number(eventId),
        partnerId: partnerSummary.id,
        tier,
        region,
        city,
        // start: new Date(Number(startTime)).toISOString(),
      };
    } else {
      confirmedBooking = {
        isCreated: true,
        customerId: user.id,
        productId: Number(productId),
        partnerId: partnerSummary.id,
        start: new Date(Number(startTime)).toISOString(),
      };
    }

    if (place) {
      confirmedBooking.place = {
        value: place,
        name: placeName,
        latitude,
        longitude,
        address,
        detail: detail
          ? detail
          : lastUsedLocation &&
            lastUsedLocation.value &&
            lastUsedLocation.value.detail, // TODO might be missed if the url was copied/ pasted to/ from another browser
      };
    } else if (!(membershipId || eventId)) {
      // Membership purchase
      confirmedBooking.location = {
        code: postalCode, //`CA ${postalCode}`,
        latitude,
        longitude,
        address,
      };
    }

    this.context.setConfirmedBooking(confirmedBooking);
  }

  componentDidMount() {
    const {
      user,
      partnerSummary,
      productsLoaded,
      partnerLoaded,
      hasTriedToAutoLogin,
      customerMembership,
    } = this.context;

    if (hasTriedToAutoLogin && !user) {
      this.props.history.push("/partners");

      return;
    }

    var query = new URLSearchParams(this.props.location.search);

    var partnerId = query.get("partner"),
      membershipId = query.get("membershipId"),
      eventId = query.get("eventId"),
      region = query.get("region"),
      city = query.get("city");

    // if (!productsLoaded && !(membershipId || eventId)) {
    this.context.loadProducts(
      user ? user.region : region || process.env.REACT_APP_REGION // Is it correct to use this env??
    );
    // }

    if (partnerLoaded && !(membershipId || eventId)) {
      this.createConfirmedBooking();
      // Is it correct to use env here??
      this.context.loadPrices(
        partnerSummary.id,
        // ?? FIXME
        user ? user.region : region || process.env.REACT_APP_REGION
      );
      this.context
        .loadCustomerActivePackage(user.id, partnerSummary.id)
        .catch((err) => {
          console.log("Error loading customer active package: ", err);
        });
    } else if (!partnerLoaded && !(membershipId || eventId)) {
      // var query = new URLSearchParams(this.props.location.search),
      //   partnerId = query.get("partner");

      // We rely on this to continue bookings process
      this.context.loadPartnerProfile(partnerId);
      // this.context.loadPrices(partnerId);
      this.context
        .loadCustomerActivePackage(user.id, partnerId)
        .catch((err) => {
          console.log("Error loading customer active package: ", err);
        });
      this.context.loadCustomerPackages(user.id, {
        page: 0,
        size: 10,
        sort: "id,desc",
      });
    } else if (membershipId || eventId) {
      // We rely on this to continue bookings process,
      // even though it's not needed for Membership or Additional Event purchase
      // FIXME Possibly not the right way
      this.context.loadPartnerProfile(partnerId || 56);

      if (eventId) {
        this.context.loadEvent(user.id, eventId);
      }

      // Why do we need Membership here?
      //if (!this.context.customerMembershipLoaded) {
      this.context
        .loadCustomerMembership(user.id)
        .catch((err) => {
          console.log("Error loading customer membership: ", err);
        })
        .finally(() => {
          this.forceUpdate();
        });
      //}
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { user, partnerSummary } = this.context;

    if (
      user &&
      user.id &&
      partnerSummary &&
      !(
        this.context.confirmedBooking && this.context.confirmedBooking.isCreated
      )
    ) {
      this.createConfirmedBooking();
    }

    // if (
    //   user &&
    //   user.id &&
    //   !this.context.paymentMethodsLoaded &&
    //   !this.context.isFetching
    // ) {
    //   // The Apple Pay JS API is available.
    //   this.context.loadPaymentMethods(
    //     user.id,
    //     window.ApplePaySession ? {} : { provider: "CARD" }
    //   );
    // }
  }

  componentWillUnmount() {
    this.context.setConfirmedBooking(null);
  }

  render() {
    const { children } = this.props;

    var query = new URLSearchParams(this.props.location.search);

    var membershipId = query.get("membershipId");
    var eventId = query.get("eventId");

    return (
      <div className="tbk-flex tbk-h-full tbk-flex-col">
        {this.context.confirmedBooking &&
        ((this.context.products &&
          this.context.products.length &&
          !membershipId) ||
          (membershipId && this.context.customerMembershipLoaded) ||
          (eventId && this.context.customerMembershipLoaded) ||
          this.context.customerMembership)
          ? children
          : null}
      </div>
    );
  }
}

export default withRouter(BookingContainer);
